import styled from "@emotion/styled";
import { upperFirst } from "es-toolkit";
import type { MouseEventHandler } from "react";

import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { IconButton } from "../buttons/Buttons.tsx";
import { FONT_WEIGHT_BOLD } from "../utilities/constants.ts";
import { ellipsise } from "../utilities/StyleHelpers.tsx";
import type { Toast } from "./types.ts";
import { mapTypeIdToIcon, useMapTypeIdToText } from "./useMapTypeId.tsx";

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  width: 100%;

  background-color: ${({ theme }) => theme.surface};

  padding: 8px 20px;
  border-radius: 5px;
`;

const Label = styled.span`
  font-weight: ${FONT_WEIGHT_BOLD};
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;

  height: 58px;
  width: 100%;

  font-size: ${({ theme }) => theme.fontSizeSmall};

  overflow: hidden;

  padding-inline-start: 10px;
`;

const SourceContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const DetailsContainer = styled.div`
  display: flex;
  gap: 10px;

  width: 100%;
  padding-block-start: 4px;
`;

const DetailHeadings = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DetailContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  overflow: hidden;
`;

const ContainedSpan = styled.span`
  ${ellipsise}
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CheckMark = styled(IconButton)`
  width: 36px;
  height: 36px;

  font-size: 18px;

  border-radius: 50%;
  margin-inline-start: -10px;

  color: ${({ theme }) => theme.generalTextIconVariant};

  &:hover {
    color: ${({ theme }) => theme.tertiary};
  }

  cursor: pointer;

  transition: color 150ms ease-in-out;
`;

const VerticalSeparator = styled.div`
  height: 40px;
  width: 1px;

  border-inline-end: 1px solid ${({ theme }) => theme.surfaceSoft};
`;

const HorizontalSeparator = styled.div`
  height: 1px;
  width: 100%;

  border-block-end: 1px solid ${({ theme }) => theme.surfaceSoft};
`;

type Props = Toast & {
  onDismiss: () => void;
};

export const ImportToast = ({ typeId, title, onDismiss }: Props) => {
  const formatMessage = useFormatMessage(formatStrings);

  const mapTypeIdToText = useMapTypeIdToText();

  const handleDismiss: MouseEventHandler<HTMLDivElement> = (event) => {
    event.stopPropagation();
    onDismiss();
  };

  return (
    <Container>
      <IconContainer onClick={handleDismiss} data-test="import-toast-dismiss-button">
        <CheckMark icon="circle-check" color="primary" data-test="import-toast-check-mark" />
      </IconContainer>
      <VerticalSeparator />
      <Content>
        <SourceContainer>
          <span>{mapTypeIdToIcon(typeId)}</span>
          <ContainedSpan>{title}</ContainedSpan>
        </SourceContainer>
        <HorizontalSeparator />
        <DetailsContainer>
          <DetailHeadings>
            <Label>{formatMessage("issue")}</Label>
            <Label>{formatMessage("fix")}</Label>
          </DetailHeadings>
          <DetailContent>
            <ContainedSpan>{formatMessage("cause", { type: upperFirst(mapTypeIdToText(typeId)) })}</ContainedSpan>
            <ContainedSpan>{formatMessage("solution", { type: mapTypeIdToText(typeId) })}</ContainedSpan>
          </DetailContent>
        </DetailsContainer>
      </Content>
    </Container>
  );
};

const formatStrings = {
  issue: {
    en: "Issue: ",
  },
  fix: {
    en: "Fix: ",
  },
  cause: {
    en: "{type} not found.",
  },
  solution: {
    en: "Link to existing, or new {type}.",
  },
} satisfies FormatMessages;
